import { logger } from '@gik/analytics/utils/logger';
import type { FallbackProps, StaticProps } from '@gik/core/api/BaseAPIConfig';
import { ISRRevalidateTimeout, baseFallback } from '@gik/core/api/BaseAPIConfig';
import type { Product } from '@gik/core/models/gik/Product';
import routes from '@gik/core/routes';
import { catchSSRError } from '@gik/core/utils/catchSSRError';
import { measureSSRPerformance } from '@gik/core/utils/measureSSRPerformance';
import { ProductPage } from '@gik/shop/pages/ProductPage/ProductPage';
import { getProductsBySlug } from '@/api/products/productsBySlug';
import { getWordPressProductSlugs } from '@/api/products/wordPressProductSlugs';
import type { PageLayoutProps } from '@/components/layout/PageLayout';
import { PageLayout } from '@/components/layout/PageLayout';
import type { GetServerSidePropsContext } from 'next';
import { useRouter } from 'next/router';
import { SWRConfig } from 'swr';

export default function ProductPageWrapper({ fallback }: FallbackProps): JSX.Element {
  const router = useRouter();
  const inkindRouteId = router.query.routeId as string;

  if (fallback.products?.[0].status == 'draft') {
    router.push(routes.organizeGiftCard);
    return null;
  }

  const slug = router.query.slug as string;

  return (
    <SWRConfig value={{ fallback }}>
      <ProductPage productSlug={slug} inkindRouteId={inkindRouteId} />
    </SWRConfig>
  );
}

ProductPageWrapper.getLayout = function getLayout(page, layoutProps: PageLayoutProps) {
  return (
    <PageLayout {...layoutProps} key={layoutProps.key}>
      {page}
    </PageLayout>
  );
};

async function _getStaticProps(context: GetServerSidePropsContext): Promise<StaticProps<Product[]>> {
  const slug = context.params.slug as string;

  // first try to get published version
  let productsBySlug = getProductsBySlug({ slug: [slug], perPage: 1 });
  let products;
  try {
    products = await productsBySlug.json;
  } catch (e) {
    logger.error('Error getting products json', e);
    // Don't throw error, allow client-side rendering with skeleton
  }

  if (!products?.length) {
    // try to fetch a drafted version of the product
    productsBySlug = getProductsBySlug({
      slug: [slug],
      perPage: 1,
      status: 'draft',
    });

    try {
      products = await productsBySlug.json;
    } catch (e) {
      logger.error('Error getting products json (draft)', e);
      // Don't throw error, allow client-side rendering with skeleton
    }
  }

  if (products?.[0]?.status == 'draft') {
    return {
      redirect: {
        destination: routes.organizeGiftCard,
        permanent: true,
      },
    };
  }

  // If we have products, return them in fallback
  if (products?.length) {
    return baseFallback(
      {
        [productsBySlug.key]: products,
      },
      undefined,
      ISRRevalidateTimeout.sitemapSlow
    );
  }

  // If no products found (API failure or actual 404), return empty fallback
  // This allows client-side rendering with NetworkStore retry mechanism
  return baseFallback({}, undefined, ISRRevalidateTimeout.sitemapSlow);
}

export const getStaticProps = catchSSRError(measureSSRPerformance(_getStaticProps, 'Product Page'));

export async function getStaticPaths() {
  // cache all the products at build time
  let paths = [];
  if (process.env.RENDER_STATIC_PAGES_AT_BUILD_TIME === 'true') {
    // first try to get published version
    let products = await getWordPressProductSlugs();
    if (!products.length) {
      // try to fetch a drafted version of the product
      products = await getWordPressProductSlugs({ allowDraft: true });
    }

    paths = products.map(product => ({
      params: { slug: product.slug },
    }));
  }

  return { paths, fallback: 'blocking' };
}
